import React from 'react';
import { Box, Typography } from '@mui/material';

interface OfficialEvaluationReportPrintProps {
  report: any;
}

export const OfficialEvaluationReportPrint: React.FC<OfficialEvaluationReportPrintProps> = ({ report }) => {
  if (!report || !report.student) return null;

  const { student, sections = {}, totals = {}, evaluators = {}, feedbacks = {}, workload, exam, roundCount = 0 } = report;
  const requiresSupervision = student.requires_supervision !== false && Number(student.requires_supervision) !== 0;

  const isCompetencyPassed = roundCount === 3 && (totals.grand || 0) >= 60;
  const isWorkloadPassed = workload ? Boolean(workload.is_passed) : false;

  let overallStatus = 'รอดำเนินการ';
  if (requiresSupervision) {
    if (roundCount === 3) {
      overallStatus = isCompetencyPassed ? 'ผ่านเกณฑ์สมรรถนะ' : 'ไม่ผ่านเกณฑ์สมรรถนะ';
    } else {
      overallStatus = `ประเมินแล้ว ${roundCount}/3 ฝ่าย (รอดำเนินการ)`;
    }
  } else if (workload) {
    overallStatus = isWorkloadPassed ? 'ผ่านเกณฑ์ภาระงาน' : 'ไม่ผ่านเกณฑ์ภาระงาน';
  }

  const printTableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '6px',
    fontSize: '8.5pt',
    color: '#000000',
    fontFamily: '"Sarabun", "TH Sarabun New", sans-serif',
  };

  const thStyle: React.CSSProperties = {
    border: '1px solid #333333',
    padding: '3px 4px',
    backgroundColor: '#f2f2f2',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#000000',
    lineHeight: 1.2,
  };

  const tdStyle: React.CSSProperties = {
    border: '1px solid #333333',
    padding: '2.5px 4px',
    color: '#000000',
    lineHeight: 1.25,
  };

  return (
    <Box className="official-print-document" sx={{ display: 'none', color: '#000000', bgcolor: '#ffffff', p: 0 }}>
      {/* 1. Header (Official Document Header) */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '11.5pt', fontWeight: 'bold', color: '#000000', lineHeight: 1.2 }}>
          คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
        </Typography>
        <Typography sx={{ fontSize: '10.5pt', fontWeight: 'bold', color: '#000000', lineHeight: 1.2 }}>
          รายงานสรุปผลการประเมินการฝึกประสบการณ์วิชาชีพครูรายบุคคล
        </Typography>
        <Typography sx={{ fontSize: '8pt', color: '#333333', lineHeight: 1.2 }}>
          ระบบบริหารจัดการการฝึกประสบการณ์วิชาชีพครู (TPMS) • วันที่พิมพ์เอกสาร: {new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>

      {/* 2. Student Information Table (ตารางข้อมูลนักศึกษา) */}
      <table style={printTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...tdStyle, width: '15%', fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>ชื่อ - สกุลนักศึกษา:</td>
            <td style={{ ...tdStyle, width: '35%' }}>{student.prefix || ''}{student.first_name} {student.last_name}</td>
            <td style={{ ...tdStyle, width: '15%', fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>รหัสนักศึกษา:</td>
            <td style={{ ...tdStyle, width: '35%', fontWeight: 'bold' }}>{student.student_code}</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>สาขาวิชา:</td>
            <td style={{ ...tdStyle }}>{student.major}</td>
            <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>หมู่เรียน:</td>
            <td style={{ ...tdStyle }}>{student.group_no || 'ไม่ระบุหมู่เรียน'}</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>สถานศึกษาฝึกสอน:</td>
            <td style={{ ...tdStyle }}>{student.school_name || '-'}</td>
            <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>รายวิชาฝึกสอน:</td>
            <td style={{ ...tdStyle }}>{student.course_code ? `${student.course_code} ` : ''}{student.course_name}</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>ประเภทรายวิชา:</td>
            <td style={{ ...tdStyle }}>{requiresSupervision ? 'รายวิชาที่ต้องรับการนิเทศ' : 'รายวิชาที่ยกเว้นการนิเทศ (ประเมินภาระงาน)'}</td>
            <td style={{ ...tdStyle, fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>ผลการประเมินสรุป:</td>
            <td style={{ ...tdStyle, fontWeight: 'bold' }}>{overallStatus}</td>
          </tr>
        </tbody>
      </table>

      {/* 3. Competency Section (ตารางคะแนนประเมินสมรรถนะ - ถ้าต้องรับการนิเทศ) */}
      {requiresSupervision && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5, mb: 0.3 }}>
            <Typography sx={{ fontSize: '8.8pt', fontWeight: 'bold', color: '#000000' }}>
              1. สรุปผลการประเมินสมรรถนะ (คะแนนเฉลี่ย 3 ฝ่าย รวม 100 คะแนนเต็ม)
            </Typography>
            <Typography sx={{ fontSize: '8pt', color: '#444444' }}>
              คณะกรรมการ: 1) วิชาชีพ: <b>{evaluators.prof || '-'}</b> 2) วิชาเอก: <b>{evaluators.spec1 || '-'}</b> 3) ครูพี่เลี้ยง: <b>{evaluators.co || '-'}</b>
            </Typography>
          </Box>
          <table style={printTableStyle}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...thStyle, width: '13%' }}>เกณฑ์ประเมิน</th>
                <th colSpan={3} style={thStyle}>สมรรถนะด้านการปฏิบัติงาน (60 คะแนน)</th>
                <th colSpan={5} style={thStyle}>สมรรถนะด้านการปฏิบัติตน (40 คะแนน)</th>
                <th rowSpan={2} style={{ ...thStyle, width: '13%' }}>คะแนนสุทธิ<br/>(100)</th>
              </tr>
              <tr>
                <th style={{ ...thStyle, width: '9%' }}>1.1 ปฏิบัติ<br/>หน้าที่ (20)</th>
                <th style={{ ...thStyle, width: '9%' }}>1.2 จัดการ<br/>เรียนรู้ (24)</th>
                <th style={{ ...thStyle, width: '9%' }}>1.3 สัมพันธ์<br/>ชุมชน (16)</th>
                <th style={{ ...thStyle, width: '7%' }}>2.1 วิชาชีพ<br/>(8)</th>
                <th style={{ ...thStyle, width: '7%' }}>2.2 ตนเอง<br/>(8)</th>
                <th style={{ ...thStyle, width: '7%' }}>2.3 ผู้รับบริการ<br/>(8)</th>
                <th style={{ ...thStyle, width: '7%' }}>2.4 เพื่อนร่วมงาน<br/>(8)</th>
                <th style={{ ...thStyle, width: '7%' }}>2.5 สังคม<br/>(8)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...tdStyle, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#f9f9f9' }}>คะแนนเฉลี่ย</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['1.1'] !== undefined ? sections['1.1'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['1.2'] !== undefined ? sections['1.2'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['1.3'] !== undefined ? sections['1.3'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['2.1'] !== undefined ? sections['2.1'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['2.2'] !== undefined ? sections['2.2'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['2.3'] !== undefined ? sections['2.3'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['2.4'] !== undefined ? sections['2.4'].toFixed(2) : '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{sections['2.5'] !== undefined ? sections['2.5'].toFixed(2) : '-'}</td>
                <td rowSpan={2} style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '10.5pt', backgroundColor: '#f9f9f9' }}>
                  {totals.grand !== undefined ? `${totals.grand.toFixed(2)}%` : '-'}
                </td>
              </tr>
              <tr>
                <td style={{ ...tdStyle, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#f9f9f9' }}>คะแนนรายส่วน</td>
                <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>
                  {totals['1'] !== undefined ? `${totals['1'].toFixed(2)} / 60` : '-'}
                </td>
                <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>
                  {totals['2'] !== undefined ? `${totals['2'].toFixed(2)} / 40` : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* 4. Workload Evaluation Table (ตารางคะแนนประเมินภาระงาน) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5, mb: 0.3 }}>
        <Typography sx={{ fontSize: '8.8pt', fontWeight: 'bold', color: '#000000' }}>
          {requiresSupervision ? '2.' : '1.'} สรุปผลการประเมินภาระงาน (Workload Task Evaluation)
        </Typography>
        {workload && (
          <Typography sx={{ fontSize: '8pt', color: '#444444' }}>
            แบบประเมิน: <b>{workload.form_title || 'แบบประเมินภาระงาน'}</b> | ผู้ประเมิน: <b>{workload.evaluator_name || 'อาจารย์นิเทศก์'}</b>
          </Typography>
        )}
      </Box>

      {workload && workload.items && workload.items.length > 0 ? (
        <table style={printTableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '6%' }}>ข้อที่</th>
              <th style={{ ...thStyle, width: '64%', textAlign: 'left', paddingLeft: '8px' }}>รายการภาระงาน / งานที่มอบหมาย</th>
              <th style={{ ...thStyle, width: '15%' }}>คะแนนเต็ม</th>
              <th style={{ ...thStyle, width: '15%' }}>คะแนนที่ได้</th>
            </tr>
          </thead>
          <tbody>
            {workload.items.map((item: any, idx: number) => (
              <tr key={item.id || idx}>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ ...tdStyle, paddingLeft: '8px' }}>
                  <b>{item.item_code ? `${item.item_code} ` : ''}</b>
                  {item.item_name || item.title || `ภาระงานชิ้นที่ ${idx + 1}`}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{item.max_score}</td>
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: item.score !== null ? 'bold' : 'normal' }}>
                  {item.score !== null && item.score !== undefined ? item.score : '-'}
                </td>
              </tr>
            ))}
            <tr style={{ backgroundColor: '#f9f9f9' }}>
              <td colSpan={2} style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', paddingRight: '12px' }}>
                รวมคะแนนประเมินภาระงานสุทธิ (คิดเป็น {workload.percentage ? workload.percentage.toFixed(1) : '0.0'}% • เกณฑ์ผ่าน ≥ 60%)
              </td>
              <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{workload.max_score}</td>
              <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '9.5pt' }}>
                {workload.total_score !== null && workload.total_score !== undefined ? workload.total_score : (workload.score ?? '-')}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table style={printTableStyle}>
          <tbody>
            <tr>
              <td style={{ ...tdStyle, textAlign: 'center', padding: '6px' }}>
                ยังไม่มีการบันทึกคะแนนภาระงานสำหรับนักศึกษารายนี้
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* 5. Comprehensive Exam Table (ตารางผลการสอบประมวลความรู้ - ถ้ามี) */}
      {(student.requires_exam || exam) && (
        <>
          <Typography sx={{ fontSize: '8.8pt', fontWeight: 'bold', color: '#000000', mt: 0.5, mb: 0.3 }}>
            {requiresSupervision ? '3.' : '2.'} ผลการสอบประมวลความรู้ (Comprehensive Exam)
          </Typography>
          {exam ? (
            <table style={printTableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '45%', textAlign: 'left', paddingLeft: '8px' }}>ชุดข้อสอบ</th>
                  <th style={{ ...thStyle, width: '15%' }}>คะแนนเต็ม</th>
                  <th style={{ ...thStyle, width: '15%' }}>คะแนนที่ได้</th>
                  <th style={{ ...thStyle, width: '12%' }}>ร้อยละ</th>
                  <th style={{ ...thStyle, width: '13%' }}>ผลการสอบ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, paddingLeft: '8px' }}>{exam.exam_title || 'การสอบประมวลความรู้'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{exam.total_points}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{exam.score}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{exam.percentage ? exam.percentage.toFixed(1) : '0.0'}%</td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{exam.is_passed ? 'ผ่านเกณฑ์' : 'ไม่ผ่านเกณฑ์'}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table style={printTableStyle}>
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, textAlign: 'center', padding: '6px' }}>
                    กำหนดให้มีการสอบประมวลความรู้ แต่นักศึกษายังไม่ได้เข้าสอบหรืออยู่ระหว่างรอส่งผลสอบ
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </>
      )}

      {/* 6. Qualitative Feedback (จัดกลุ่มข้อเสนอแนะเป็นข้อๆ สรุปสั้นๆ กระชับ) */}
      {requiresSupervision && (
        <Box sx={{ mt: 0.5, mb: 1 }}>
          <Typography sx={{ fontSize: '8.8pt', fontWeight: 'bold', color: '#000000', mb: 0.3 }}>
            {(student.requires_exam || exam) ? '4.' : '3.'} ความคิดเห็นและข้อเสนอแนะเชิงคุณภาพของคณะกรรมการนิเทศ
          </Typography>
          <Box sx={{ border: '1px solid #333333', p: '4px 8px', fontSize: '8pt', lineHeight: 1.3 }}>
            <div>• <b>อาจารย์นิเทศก์ (วิชาชีพ):</b> {feedbacks.prof ? feedbacks.prof.trim() : 'ไม่มีข้อเสนอแนะเพิ่มเติม'}</div>
            <div>• <b>อาจารย์นิเทศก์ (วิชาเอก):</b> {feedbacks.spec1 ? feedbacks.spec1.trim() : 'ไม่มีข้อเสนอแนะเพิ่มเติม'}</div>
            <div>• <b>ครูพี่เลี้ยง (สถานศึกษา):</b> {feedbacks.co ? feedbacks.co.trim() : 'ไม่มีข้อเสนอแนะเพิ่มเติม'}</div>
          </Box>
        </Box>
      )}

      {/* 7. Official Signatures Block (ช่องลงนามรับรองเอกสาร) */}
      <table style={{ width: '100%', marginTop: '10px', fontSize: '8pt', border: 'none', color: '#000000' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', textAlign: 'center', verticalAlign: 'top', border: 'none' }}>
              ลงชื่อ........................................................................... ผู้รายงาน<br />
              ({workload?.evaluator_name || 'อาจารย์นิเทศก์ / เจ้าหน้าที่งานฝึกฯ'})<br />
              วันที่ ........ เดือน .................... พ.ศ. ............
            </td>
            <td style={{ width: '50%', textAlign: 'center', verticalAlign: 'top', border: 'none' }}>
              ลงชื่อ........................................................................... ผู้รับรอง<br />
              (หัวหน้าฝ่ายฝึกประสบการณ์วิชาชีพครู)<br />
              วันที่ ........ เดือน .................... พ.ศ. ............
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

export default OfficialEvaluationReportPrint;
